import { z } from "zod";

import { attachmentClassificationSchema } from "./attachments";
import { syntheticAttachmentCaseSchema } from "./fixtures";

const trelloIdSchema = z
  .string()
  .regex(/^[a-f0-9]{24}$/, "Expected a synthetic 24-character Trello ID.");

const expectedAttachmentSchema = attachmentClassificationSchema.extend({
  archiveFilename: z.string().min(1).nullable(),
});

const syntheticAttachmentSchema = z
  .object({
    cardId: trelloIdSchema,
    cases: z.array(syntheticAttachmentCaseSchema).min(1),
    expected: expectedAttachmentSchema,
    id: trelloIdSchema,
    isUpload: z.boolean(),
    name: z.string(),
    url: z.string().min(1).nullable(),
  })
  .strict()
  .superRefine((attachment, context) => {
    if (
      attachment.expected.disposition === "copy_candidate" &&
      attachment.expected.archiveFilename === null
    ) {
      context.addIssue({
        code: "custom",
        message: "Copy candidates require an expected archive filename.",
        path: ["expected", "archiveFilename"],
      });
    }

    if (
      attachment.expected.disposition !== "copy_candidate" &&
      attachment.expected.archiveFilename !== null
    ) {
      context.addIssue({
        code: "custom",
        message: "Non-copied attachments cannot claim an archive filename.",
        path: ["expected", "archiveFilename"],
      });
    }
  });

const REQUIRED_MIX_CASES = [
  "trello_upload",
  "safe_https",
  "protected_drive",
  "protected_dropbox",
  "protected_onedrive",
  "missing",
  "duplicate_filename",
  "unicode_filename",
] as const;

export const syntheticAttachmentMixFixtureSchema = z
  .object({
    attachments: z.array(syntheticAttachmentSchema).length(16),
    expected: z
      .object({
        counts: z
          .object({
            attachments: z.literal(16),
            cards: z.literal(8),
          })
          .strict(),
        outcome: z.literal("complete_with_warnings"),
        warningCodes: z.array(z.literal("ATTACHMENT_URL_MISSING")).length(1),
      })
      .strict(),
    fixtureId: z.literal("attachment-mix"),
    provenance: z.literal("generated-only"),
    schemaVersion: z.literal(1),
    synthetic: z.literal(true),
  })
  .strict()
  .superRefine((fixture, context) => {
    const attachmentIds = fixture.attachments.map(({ id }) => id);
    const cardIds = new Set(fixture.attachments.map(({ cardId }) => cardId));
    const coveredCases = new Set(
      fixture.attachments.flatMap(({ cases }) => cases),
    );

    if (new Set(attachmentIds).size !== attachmentIds.length) {
      context.addIssue({
        code: "custom",
        message: "Attachment IDs must be unique.",
        path: ["attachments"],
      });
    }

    if (cardIds.size !== fixture.expected.counts.cards) {
      context.addIssue({
        code: "custom",
        message: "Declared card count must match unique attachment card IDs.",
        path: ["expected", "counts", "cards"],
      });
    }

    const missingCases = REQUIRED_MIX_CASES.filter(
      (fixtureCase) => !coveredCases.has(fixtureCase),
    );
    if (missingCases.length > 0) {
      context.addIssue({
        code: "custom",
        message: `Attachment mix is missing cases: ${missingCases.join(", ")}.`,
        path: ["attachments"],
      });
    }
  });

export type SyntheticAttachmentMixFixture = z.infer<
  typeof syntheticAttachmentMixFixtureSchema
>;
